import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const go = (next: number) => setActive((next + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    timer.current = setInterval(() => {
      setActive((v) => (v + 1) % heroSlides.length);
    }, 6000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const resetTimer = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      setActive((v) => (v + 1) % heroSlides.length);
    }, 6000);
  };

  const slide = heroSlides[active];

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950">
      {heroSlides.map((s, i) => (
        <div
          key={s.videoId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={`${s.title} 焦点图`}
            title={`${s.title} - 高清视频在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/55 to-primary-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/40" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-end md:items-center">
        <div className="w-full pb-16 md:pb-0">
          <div key={slide.videoId} className="max-w-2xl animate-fadeUp">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {slide.tags.map((t) => (
                <span
                  key={t}
                  className="px-2.5 py-1 rounded-md bg-secondary-500/95 text-background-50 text-xs font-medium whitespace-nowrap"
                >
                  {t}
                </span>
              ))}
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-background-50 tracking-tight">
              {slide.title}
            </h2>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 max-w-xl leading-relaxed">
              {slide.subtitle}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={`/detail/${slide.videoId}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to="/category/drama"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-100/50 text-background-50 text-sm font-semibold hover:bg-background-50/10 whitespace-nowrap cursor-pointer transition-colors"
              >
                浏览全部视频
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 md:bottom-8 left-0 right-0 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {heroSlides.map((s, i) => (
            <button
              key={s.videoId}
              type="button"
              onClick={() => {
                go(i);
                resetTimer();
              }}
              aria-label={`切换到 ${s.title}`}
              className={`h-1.5 rounded-full cursor-pointer transition-all ${
                i === active ? "w-8 bg-secondary-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              go(active - 1);
              resetTimer();
            }}
            aria-label="上一张"
            className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => {
              go(active + 1);
              resetTimer();
            }}
            aria-label="下一张"
            className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}